import { SubmitHandler, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { CreateCompanyBenefitTaxDto } from "../../../../../../models/benefit-tax.dto";
import {
	APP_CONFIG,
	formatRate,
	IAddEditTableForm,
} from "../../../../../../helpers/appHelpers";
import useEditFormStore from "../../../../../../state-management/useEditFormStore";
import {
	useCreateCompanyBenefitTax,
	useUpdateCompanyBenefitTax,
} from "../../../../../../services/company-benefit-tax.service";
import ModalComponent from "../../../../../../components/modals/ModalFormComponent";
import Label from "../../../../../../components/form_fields/Label";
import MoneyInput from "../../../../../../components/form_fields/MoneyInput";
import { useGetBenefitType } from "../../../../../../services/benefit-type.service";
import { useManageTableWithSearchParamsData } from "../../../../../../helpers/useManageTable";
import RateComponent from "../../../../../../components/form_fields/RateComponent";
import APISelectField from "../../../../../../components/form_fields/APISelectField";
import { PATHS } from "../../../../../../routes/routes.paths";

type IFormInputs = Omit<CreateCompanyBenefitTaxDto, "isFinalTax"> & {
	isFinalTax: string;
};

type ICompanyBenefitTaxRuleModalForm = IAddEditTableForm & {
	companyId: number;
	reloadTableData?: () => void;
};

const CompanyBenefitTaxRuleModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	companyId,
	reloadTableData,
}: ICompanyBenefitTaxRuleModalForm) => {
	const { formDataValues } = useEditFormStore();
	const [uploading, setUploading] = useState(false);

	const {
		register,
		watch,
		handleSubmit,
		setValue,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			minTaxableAmount: isEditForm ? formDataValues?.minTaxableAmount : "",
			maxTaxableAmount: isEditForm ? formDataValues?.maxTaxableAmount : "",
			rateComponent: isEditForm
				? ((Number(formDataValues?.rateComponent) * 100) as any)
				: "",
			fixedComponent: isEditForm ? formDataValues?.fixedComponent : "",
			benefitTypeInstanceId: isEditForm
				? formDataValues?.benefitTypeInstanceId
				: undefined,
			industrySetting: isEditForm ? formDataValues?.industrySetting : "",
			isFinalTax: isEditForm ? (formDataValues?.isFinalTax ? "yes" : "no") : "",
		},
	});

	const maxTaxableAmount = watch("maxTaxableAmount");

	const { data: benefitTypes, isLoading: benefitTypesLoading } =
		useGetBenefitType({
			limit: APP_CONFIG.PAGE_LIMIT,
			page: 1,
			orderBy: "name:asc" as any,
		});

	useEffect(() => {
		setValue(
			"benefitTypeInstanceId",
			isEditForm ? formDataValues?.benefitTypeInstanceId : "",
		);
	}, [benefitTypesLoading]);

	const { mutate: createBenefitTax } = useCreateCompanyBenefitTax();
	const { mutate: updateBenefitTax } = useUpdateCompanyBenefitTax();
	const { addNewTableData } = useManageTableWithSearchParamsData();

	const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
		setUploading(true);

		formatRate(data);
		data.isFinalTax = data.isFinalTax === "yes";

		if (isEditForm) {
			updateBenefitTax(
				{ companyId, id: formDataValues?.id, payload: data },
				{
					onSuccess: (updatedData) => {
						try {
							tableAPIRef?.current.updateRows([updatedData.data]);
						} catch (error) {
							console.error("Error updating table rows:", error);
							reloadTableData?.();
						}
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			createBenefitTax(
				{ companyId, payload: data },
				{
					onSuccess: () => {
						addNewTableData();
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	const handleClose = () => {
		setOpen(false);
		reset();
	};

	return (
		<ModalComponent
			width={775}
			open={open}
			title={title}
			handleClose={handleClose}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="grid grid-cols-2 gap-x-4 gap-y-6">
					<div className="w-full">
						<Label title="Benefit Type" for="benefitTypeInstanceId" />
						<APISelectField
							key="benefitTypeInstanceId"
							attributes={{ id: "benefitTypeInstanceId" }}
							placeholder="Select Type"
							register={register}
							name="benefitTypeInstanceId"
							options={benefitTypes?.data || []}
							pathname={PATHS.ADMIN_SUB_SYSTEM_CONFIGURATIONS.BENEFIT_TYPES}
							required="Select benefit type here"
							error={errors.benefitTypeInstanceId}
							valueAsNumber
						/>
						{errors.benefitTypeInstanceId && (
							<p className="text-red-500 text-sm mt-1">
								{errors.benefitTypeInstanceId.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Industry Settings" for="industrySetting" />
						<select
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm mb-2 ${
								errors.industrySetting
									? "border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
							{...register("industrySetting", {
								required: "Select industry setting here",
							})}
						>
							<option value="">Select Setting</option>
							<option value="APPLICABLE">Applicable</option>
							<option value="NON_APPLICABLE">Non Applicable</option>
							<option value="EXEMPT">Exempt</option>
							<option value="NONE">None</option>
						</select>
						{errors.industrySetting && (
							<p className="text-red-500 text-sm mt-1">
								{errors.industrySetting.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Min Taxable Amount" for="minTaxableAmount" />
						<MoneyInput
							attributes={{ id: "minTaxableAmount", placeholder: "0" }}
							register={register}
							name="minTaxableAmount"
							setValue={setValue}
							defaultValue={
								isEditForm ? formDataValues?.minTaxableAmount : undefined
							}
							required="Enter minimum taxable amount"
							validate={(value: number) => {
								if (maxTaxableAmount && value > maxTaxableAmount) {
									return "Cannot be more than the max taxable value";
								}
								return true;
							}}
							error={errors.minTaxableAmount}
						/>
						{errors.minTaxableAmount && (
							<p className="text-red-500 text-sm mt-1">
								{errors.minTaxableAmount.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Max Taxable Amount" for="maxTaxableAmount" />
						<MoneyInput
							attributes={{ id: "maxTaxableAmount", placeholder: "0" }}
							register={register}
							name="maxTaxableAmount"
							setValue={setValue}
							defaultValue={
								isEditForm ? formDataValues?.maxTaxableAmount : undefined
							}
							required="Enter maximum taxable amount"
							error={errors.maxTaxableAmount}
						/>
						{errors.maxTaxableAmount && (
							<p className="text-red-500 text-sm mt-1">
								{errors.maxTaxableAmount.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Rate Component" for="rateComponent" />
						<RateComponent
							attributes={{ id: "rateComponent" }}
							register={register}
							name="rateComponent"
							required="Enter rate component"
							error={errors.rateComponent}
						/>
						{errors.rateComponent && (
							<p className="text-red-500 text-sm mt-1">
								{errors.rateComponent.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Fixed Component" for="fixedComponent" optional />
						<MoneyInput
							attributes={{ id: "fixedComponent", placeholder: "0" }}
							register={register}
							name="fixedComponent"
							setValue={setValue}
							defaultValue={
								isEditForm ? formDataValues?.fixedComponent : undefined
							}
							required={false}
							error={errors.fixedComponent}
						/>
					</div>
					<div className="col-span-2">
						<div className="flex space-x-10 text-base">
							<Label title="Final Tax" for="isFinalTax" />
							<div className="flex space-x-3 items-center">
								<input
									{...register("isFinalTax")}
									defaultChecked={
										isEditForm && formDataValues?.isFinalTax === true
									}
									type="radio"
									value="yes"
									id="isFinalTax"
								/>
								<label htmlFor="isFinalTax">Yes</label>
							</div>
							<div className="flex space-x-3 items-center">
								<input
									{...register("isFinalTax")}
									defaultChecked={
										isEditForm && formDataValues?.isFinalTax === false
									}
									type="radio"
									value="no"
									id="notFinalTax"
								/>
								<label htmlFor="notFinalTax">No</label>
							</div>
						</div>
					</div>
				</div>

				<div className="w-full flex justify-end mt-8">
					<button
						type="submit"
						disabled={uploading}
						className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
					>
						{uploading
							? "Uploading..."
							: isEditForm
								? "Update Benefit Rule"
								: "Add Benefit Rule"}
					</button>
				</div>
			</form>
		</ModalComponent>
	);
};

export default CompanyBenefitTaxRuleModalForm;
